import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRM } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, ArrowLeft } from "lucide-react";

export default function Points() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/points/transactions")
      .then((r) => setTxs(r.data))
      .catch(() => setTxs([]))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16" data-testid="points-page">
      <Link to="/dashboard" className="text-sm text-[#4a6e77] hover:text-[#005870] inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>
      <h1 className="mt-3 font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">{t("points.title")}</h1>

      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6 flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-[#00868e]/10 text-[#00868e] flex items-center justify-center">
          <Sparkles size={22} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">{t("points.balance")}</div>
          <div className="text-3xl font-semibold text-[#005870]" data-testid="points-balance">
            {formatRM(Number(user.points_balance || 0))}
          </div>
        </div>
      </div>

      <h2 className="mt-10 font-serif-cn text-xl text-[#003645] font-semibold">{t("points.history")}</h2>
      <div className="mt-4 rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid="points-history">
        {loading ? (
          <div className="p-6 text-[#4a6e77]">{t("common.loading")}</div>
        ) : txs.length === 0 ? (
          <div className="p-6 text-[#4a6e77]">{t("common.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Type</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Order</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Note</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb] text-right">Amount</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Date</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((tx) => (
                <tr key={tx.id}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">
                    {t(`points.types.${tx.type}`, tx.type)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">{tx.order_no || "-"}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{tx.note || "-"}</td>
                  <td className={`py-3 px-4 border-b border-[#e5ebeb] text-right font-semibold ${tx.amount >= 0 ? "text-[#00868e]" : "text-red-700"}`}>
                    {tx.amount >= 0 ? "+" : ""}{formatRM(tx.amount)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">
                    {new Date(tx.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
